(function () {
  'use strict';

  var VERBOSE = true;
  function log() {
    if (!VERBOSE) return;
    var args = ['[BXGY]'].concat(Array.prototype.slice.call(arguments));
    console.debug.apply(console, args);
  }

  function getNowCentral() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  }

  var CONFIG_SELECTOR = '#bxgy-offers-config';
  var PICKER_TEMPLATE = '#bxgy-picker-template';
  var CARD_TEMPLATE = '#bxgy-product-card-template';
  var DEBOUNCE_MS = 500;

  function extractNumericId(gid) {
    if (typeof gid === 'number') return gid;
    var match = String(gid).match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  class BxgyManager {
    constructor() {
      log('Initializing BxgyManager');
      var configEl = document.querySelector(CONFIG_SELECTOR);
      if (!configEl) {
        log('No config element found (' + CONFIG_SELECTOR + '), aborting');
        return;
      }

      var config;
      try {
        config = JSON.parse(configEl.textContent);
        log('Config parsed successfully:', config);
      } catch (e) {
        console.error('[BXGY] Failed to parse config', e);
        return;
      }

      this.currentMarket = config.currentMarket || '';
      this.currencySymbol = config.currencySymbol || '$';
      this.t = config.translations || {};
      this.customerTags = config.customerTags || [];
      this.customerLoggedIn = config.customerLoggedIn || false;
      this.discountMessages = config.discountMessages || {};
      this.pickerHeadings = config.pickerHeadings || {};
      this.rewardVariantSkuMap = config.rewardVariantSkuMap || {};
      log('Market:', this.currentMarket, '| Customer logged in:', this.customerLoggedIn, '| Customer tags:', this.customerTags);

      var marketFiltered = this._filterByMarket(config.offers || []);
      log('Offers after market filter:', marketFiltered.length, 'of', (config.offers || []).length);
      this.offers = this._filterByCustomerTags(marketFiltered);
      log('Offers after customer tag filter:', this.offers.length, 'of', marketFiltered.length);

      if (!this.offers.length) {
        log('No eligible offers remaining, aborting');
        return;
      }

      log('Active offers:', this.offers.map(function (o) { return o.id + ' (' + o.title + ')'; }));

      var self2 = this;
      // Pre-compute numeric IDs for qualifying products and reward variants
      this.offers.forEach(function (offer) {
        offer._qualifyingIds = (offer.qualifyingProductIds || [])
          .map(extractNumericId)
          .filter(Boolean);
        offer._rewardVariantIds = (offer.rewardProductVariantIds || [])
          .map(extractNumericId)
          .filter(Boolean);
        offer._rewardVariantProductMap = offer.rewardVariantProductMap || {};
        // Build a variant→handle map from rewardProducts (fallback when rewardVariantProductMap not yet synced)
        offer._rewardVariantHandleMap = {};
        // Build a variant→sku map for this offer from the global rewardVariantSkuMap
        offer._rewardVariantSkuMap = {};
        (offer.rewardProducts || []).forEach(function (rp) {
          var vid = rp.numericVariantId || extractNumericId(rp.variantId);
          if (vid && rp.productHandle) {
            offer._rewardVariantHandleMap[vid] = rp.productHandle;
          }
          if (vid != null) {
            var globalSku = self2.rewardVariantSkuMap[vid] || self2.rewardVariantSkuMap[String(vid)];
            if (globalSku) offer._rewardVariantSkuMap[vid] = globalSku;
          }
        });
        log('Offer', offer.id, '| qualifying IDs:', offer._qualifyingIds, '| reward variant IDs:', offer._rewardVariantIds);
      });

      this._selfMutating = false;
      this._evaluating = false;
      this._debounceTimer = null;
      this.optOuts = new Map();
      this._rewardRemoved = new Map();
      this._sessionOptOuts = this._loadSessionOptOuts();

      var self = this;
      document.addEventListener('bxgy:reward-removed', function (e) {
        var offerId = e.detail && e.detail.offerId;
        var variantId = e.detail && e.detail.variantId;
        var sku = e.detail && e.detail.sku;
        if (offerId) {
          log('Reward manually removed for offer:', offerId, '| variantId:', variantId, '| sku:', sku);
          // Resolve SKU: prefer event detail, fall back to per-offer map
          if (!sku && variantId != null) {
            var offer = self._findOfferById(offerId);
            if (offer && offer._rewardVariantSkuMap) {
              sku = offer._rewardVariantSkuMap[variantId] || offer._rewardVariantSkuMap[String(variantId)] || null;
            }
          }
          // Record decline on parent X line item, then mark as removed for opt-out logic.
          // Note: only the cart delete button dispatches this event today, so a quantity
          // decrement on a same-variant merged X+Y line is a known untracked case.
          self._recordDeclinedSku(offerId, sku).then(function () {
            self._rewardRemoved.set(String(offerId), { variantId: variantId });
          });
        }
      });

      log('Installing cart intercept');
      this.installCartIntercept();
      log('Installing change selection handler');
      this.installChangeSelectionHandler();
      log('Running initial evaluate');
      this.evaluate();

      // MutationObserver: re-inject CTAs when cart drawer HTML is replaced
      this._ctaObserverTimer = null;
      var cartDrawerEl = document.getElementById('CartDrawer');
      if (cartDrawerEl) {
        var observer = new MutationObserver(function () {
          clearTimeout(self._ctaObserverTimer);
          self._ctaObserverTimer = setTimeout(function () {
            log('MutationObserver: CartDrawer changed, re-injecting CTAs');
            self._injectFreeGiftCTAs(false);
          }, 100);
        });
        observer.observe(cartDrawerEl, { childList: true, subtree: true });
        log('MutationObserver installed on #CartDrawer');
      }
    }

    /* ── Filtering ── */

    _filterByMarket(offers) {
      var market = this.currentMarket;
      return offers.filter(function (offer) {
        if (offer.status !== 'active') {
          log('Offer', offer.id, 'skipped: status is', offer.status, '(not active)');
          return false;
        }

        var allowed = offer.allowedMarkets || [];
        if (allowed.length > 0 && allowed.indexOf(market) === -1) {
          log('Offer', offer.id, 'skipped: market', market, 'not in allowedMarkets', allowed);
          return false;
        }

        return true;
      });
    }

    _filterByCustomerTags(offers) {
      var loggedIn = this.customerLoggedIn;
      var tags = this.customerTags;
      return offers.filter(function (offer) {
        var requiredTags = offer.customerTags || [];
        if (requiredTags.length === 0) return true;
        if (!loggedIn) {
          log('Offer', offer.id, 'skipped: requires tags', requiredTags, 'but customer not logged in');
          return false;
        }
        for (var i = 0; i < requiredTags.length; i++) {
          if (tags.indexOf(requiredTags[i]) !== -1) return true;
        }
        log('Offer', offer.id, 'skipped: customer tags', tags, 'do not match required', requiredTags);
        return false;
      });
    }

    /* ── Session Storage Opt-Outs ── */

    _loadSessionOptOuts() {
      try {
        var data = sessionStorage.getItem('bxgy_reward_optouts');
        var parsed = data ? JSON.parse(data) : {};
        log('Loaded session opt-outs:', parsed);
        return parsed;
      } catch (e) {
        log('Failed to load session opt-outs:', e);
        return {};
      }
    }

    _saveSessionOptOut(offerId, variantId, hash) {
      var key = String(offerId);
      this._sessionOptOuts[key] = { variantId: variantId, hash: hash };
      log('Saving session opt-out for offer:', key, '| variantId:', variantId, '| hash:', hash);
      try {
        sessionStorage.setItem('bxgy_reward_optouts', JSON.stringify(this._sessionOptOuts));
      } catch (e) {
        log('Failed to save session opt-out:', e);
      }
    }

    _removeSessionOptOut(offerId) {
      var key = String(offerId);
      delete this._sessionOptOuts[key];
      log('Removed session opt-out for offer:', key);
      try {
        sessionStorage.setItem('bxgy_reward_optouts', JSON.stringify(this._sessionOptOuts));
      } catch (e) {
        log('Failed to remove session opt-out:', e);
      }
    }

    /* ── Cart Intercept ── */

    installCartIntercept() {
      var self = this;
      var originalFetch = window.fetch;

      window.fetch = function () {
        var url = arguments[0];
        var opts = arguments[1];
        var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');

        var isCartMutation =
          urlStr.indexOf('/cart/add') !== -1 ||
          urlStr.indexOf('/cart/change') !== -1 ||
          urlStr.indexOf('/cart/update') !== -1 ||
          urlStr.indexOf('/cart/clear') !== -1;

        if (isCartMutation) {
          log('Intercepted fetch cart mutation:', urlStr, '| selfMutating:', self._selfMutating);
        }

        var result = originalFetch.apply(window, arguments);

        if (isCartMutation && !self._selfMutating) {
          result.then(function () {
            log('Cart mutation completed, scheduling debounced evaluate');
            self.debouncedEvaluate();
          }).catch(function () {});
        }

        return result;
      };

      // Also intercept XMLHttpRequest for older Shopify themes
      var originalXHROpen = XMLHttpRequest.prototype.open;
      var originalXHRSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function () {
        this._bxgyUrl = arguments[1] || '';
        return originalXHROpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        var urlStr = xhr._bxgyUrl || '';

        var isCartMutation =
          urlStr.indexOf('/cart/add') !== -1 ||
          urlStr.indexOf('/cart/change') !== -1 ||
          urlStr.indexOf('/cart/update') !== -1 ||
          urlStr.indexOf('/cart/clear') !== -1;

        if (isCartMutation) {
          log('Intercepted XHR cart mutation:', urlStr, '| selfMutating:', self._selfMutating);
        }

        if (isCartMutation && !self._selfMutating) {
          xhr.addEventListener('load', function () {
            log('XHR cart mutation completed, scheduling debounced evaluate');
            self.debouncedEvaluate();
          });
        }

        return originalXHRSend.apply(this, arguments);
      };
    }

    /* ── Evaluation ── */

    debouncedEvaluate() {
      var self = this;
      log('Debounced evaluate requested (will fire in', DEBOUNCE_MS, 'ms)');
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(function () {
        log('Debounce timer fired, calling evaluate');
        self.evaluate();
      }, DEBOUNCE_MS);
    }

    evaluate() {
      if (this._evaluating) {
        log('Evaluate skipped: already evaluating, will re-evaluate after');
        this._pendingReevaluate = true;
        return;
      }
      this._evaluating = true;
      log('Evaluate started: fetching /cart.js');

      var self = this;

      fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          log('Cart fetched:', cart.items.length, 'items, total:', cart.total_price);
          log('Cart items:', cart.items.map(function (i) {
            return { id: i.variant_id, product_id: i.product_id, qty: i.quantity, props: i.properties };
          }));
          return self._processOffers(cart, 0);
        })
        .then(function () {
          log('Evaluate completed');
          self._evaluating = false;
          document.dispatchEvent(new CustomEvent('bxgy:evaluate-done'));
          self._injectFreeGiftCTAs(true);
          if (self._pendingReevaluate) {
            self._pendingReevaluate = false;
            log('Pending re-evaluate found, scheduling');
            self.debouncedEvaluate();
          }
        })
        .catch(function (err) {
          console.error('[BXGY] Evaluation error', err);
          self._evaluating = false;
          document.dispatchEvent(new CustomEvent('bxgy:evaluate-done'));
          if (self._pendingReevaluate) {
            self._pendingReevaluate = false;
            log('Pending re-evaluate found after error, scheduling');
            self.debouncedEvaluate();
          }
        });
    }

    _processOffers(cart, index) {
      if (index >= this.offers.length) {
        log('All offers processed');
        return Promise.resolve();
      }

      var self = this;
      var offer = this.offers[index];
      log('Processing offer', index + 1, 'of', this.offers.length, '| id:', offer.id, '| title:', offer.title);

      return this._evaluateOffer(cart, offer).then(function (mutated) {
        log('Offer', offer.id, 'evaluation result: mutated =', mutated);
        if (mutated) {
          log('Cart was mutated, re-fetching /cart.js before next offer');
          return fetch('/cart.js', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          })
            .then(function (res) { return res.json(); })
            .then(function (updatedCart) {
              log('Cart re-fetched after mutation:', updatedCart.items.length, 'items');
              return self._processOffers(updatedCart, index + 1);
            });
        }
        return self._processOffers(cart, index + 1);
      });
    }

    _evaluateOffer(cart, offer) {
      // Schedule filtering
      var now = getNowCentral();
      if (offer.startsAt && new Date(offer.startsAt) > now) {
        log('Offer', offer.id, '| not started yet, skipping');
        return Promise.resolve(false);
      }
      if (offer.endsAt && new Date(offer.endsAt) < now) {
        log('Offer', offer.id, '| ended, skipping');
        return Promise.resolve(false);
      }

      var qualifyingCount = this.countQualifyingItems(cart, offer);
      var existingRewards = this.getExistingRewards(cart, offer);
      var existingCount = existingRewards.reduce(function (sum, item) { return sum + item.quantity; }, 0);

      log('Offer', offer.id, '| qualifyingCount:', qualifyingCount, '| existingRewards:', existingCount);

      var minQualifying = offer.minQualifyingQuantity || 1;
      var targetQty = 0;
      if (qualifyingCount >= minQualifying) {
        targetQty = qualifyingCount * (offer.rewardQuantity || 1);

        if (offer.maxUsesPerOrder && offer.maxUsesPerOrder > 0) {
          targetQty = Math.min(targetQty, offer.maxUsesPerOrder);
        }
      }

      log('Offer', offer.id, '| targetQty:', targetQty, '(rewardQuantity:', offer.rewardQuantity, ', minQualifyingQuantity:', minQualifying, ', rewardSelectionMode:', offer.rewardSelectionMode, ', maxUsesPerOrder:', offer.maxUsesPerOrder, ')');

      // Check opt-out (customer dismissed the picker)
      var optedOut = this.optOuts.get(offer.id);
      if (optedOut) {
        var currentQualHash = this._qualifyingHash(cart, offer);
        log('Offer', offer.id, '| opt-out detected | stored hash:', optedOut, '| current hash:', currentQualHash);
        if (optedOut !== currentQualHash) {
          log('Offer', offer.id, '| qualifying items changed, clearing opt-out');
          this.optOuts.delete(offer.id);
        } else {
          log('Offer', offer.id, '| still opted out, skipping');
          return Promise.resolve(false);
        }
      }

      // Check session-persisted opt-outs (survives page refresh)
      var sessionOpt = this._sessionOptOuts[String(offer.id)];
      if (sessionOpt) {
        var currentQualHash = this._qualifyingHash(cart, offer);
        if (sessionOpt.hash !== currentQualHash) {
          log('Offer', offer.id, '| session opt-out cleared (qualifying items changed) | stored hash:', sessionOpt.hash, '| current:', currentQualHash);
          this._removeSessionOptOut(offer.id);
        } else {
          log('Offer', offer.id, '| session opt-out active | variantId:', sessionOpt.variantId, '| skipping');
          return Promise.resolve(false);
        }
      }

      // Check if reward was manually removed by user (from cart delete button)
      if (this._rewardRemoved.has(String(offer.id))) {
        var removedData = this._rewardRemoved.get(String(offer.id));
        var hash = this._qualifyingHash(cart, offer);
        log('Offer', offer.id, '| reward was manually removed by user | variantId:', removedData.variantId, '| setting opt-out');
        this._rewardRemoved.delete(String(offer.id));
        this.optOuts.set(offer.id, hash);
        this._saveSessionOptOut(offer.id, removedData.variantId, hash);
        return Promise.resolve(false);
      }

      if (targetQty > 0 && existingCount < targetQty) {
        var mode = offer.rewardSelectionMode || 'all';
        var parentInfo = this._getParentLineKey(cart, offer);
        var parentLineKey = parentInfo.key;
        var parentProductId = parentInfo.productId;
        var parentHandle = parentInfo.handle;
        log('Offer', offer.id, '| need to add rewards: targetQty', targetQty, ', existingCount', existingCount, '| mode:', mode, '| parentLineKey:', parentLineKey, '| parentProductId:', parentProductId, '| parentHandle:', parentHandle);

        if (mode === 'customer_chooses') {
          var remainingQty = targetQty - existingCount;
          var maxQty = offer.maxRewardSelections > 0 ? Math.min(offer.maxRewardSelections, remainingQty) : remainingQty;
          log('Offer', offer.id, '| showing reward picker (customer_chooses) | maxQty:', maxQty, '| remainingQty:', remainingQty);
          this.showRewardPicker(offer, maxQty, this._qualifyingHash(cart, offer), undefined, parentLineKey, parentProductId, parentHandle);
          return Promise.resolve(false);
        }

        log('Offer', offer.id, '| adding all reward variants');
        return this.addRewardsAll(offer, parentLineKey, parentProductId, parentHandle).then(function (added) {
          return added;
        });
      }

      if (existingCount > targetQty) {
        var excess = existingCount - targetQty;
        log('Offer', offer.id, '| need to remove excess rewards:', excess, '(existingCount:', existingCount, ', targetQty:', targetQty, ')');
        return this.removeRewards(cart, offer, excess);
      }

      // For 'all' mode: verify per-variant composition hasn't been tampered with
      // (e.g. user removed product A via DevTools and increased product B quantity to keep total the same)
      var mode = offer.rewardSelectionMode || 'all';
      if (mode === 'all' && existingCount === targetQty && targetQty > 0) {
        var allowedVids = {};
        offer._rewardVariantIds.forEach(function (vid) {
          allowedVids[String(vid)] = true;
        });
        var tampered = false;
        for (var ri = 0; ri < existingRewards.length; ri++) {
          var reward = existingRewards[ri];
          // In 'all' mode, each reward should have qty 1
          if (reward.quantity > 1) {
            tampered = true;
            log('Offer', offer.id, '| tamper detected: variant', reward.variant_id, 'has qty', reward.quantity, '(expected 1)');
            break;
          }
          // Check for unexpected variant IDs
          if (!allowedVids[String(reward.variant_id)]) {
            tampered = true;
            log('Offer', offer.id, '| tamper detected: unexpected variant', reward.variant_id);
            break;
          }
        }
        if (tampered) {
          log('Offer', offer.id, '| reward composition tampered, removing all and re-adding');
          var self = this;
          return this.removeRewards(cart, offer, existingCount).then(function () {
            var parentInfo = self._getParentLineKey(cart, offer);
            return self.addRewardsAll(offer, parentInfo.key, parentInfo.productId, parentInfo.handle);
          });
        }
      }

      log('Offer', offer.id, '| no action needed (existingCount:', existingCount, ', targetQty:', targetQty, ')');
      return Promise.resolve(false);
    }

    /* ── Helpers ── */

    _qualifyingHash(cart, offer) {
      var ids = [];
      cart.items.forEach(function (item) {
        if (item.properties && item.properties._bxgy_reward === 'true') return;
        if (offer._qualifyingIds.indexOf(item.product_id) !== -1) {
          ids.push(item.product_id + ':' + item.quantity);
        }
      });
      return ids.sort().join(',');
    }

    countQualifyingItems(cart, offer) {
      var count = 0;
      cart.items.forEach(function (item) {
        if (item.properties && item.properties._bxgy_reward === 'true') {
          log('Offer', offer.id, '| skipping reward item variant:', item.variant_id, 'from qualifying count');
          return;
        }
        if (offer._qualifyingIds.indexOf(item.product_id) !== -1) {
          log('Offer', offer.id, '| qualifying item found: product', item.product_id, ', variant', item.variant_id, ', qty:', item.quantity);
          count += item.quantity;
        }
      });
      log('Offer', offer.id, '| total qualifying count:', count);
      return count;
    }

    _getParentLineKey(cart, offer) {
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.properties && item.properties._bxgy_reward === 'true') continue;
        if (offer._qualifyingIds.indexOf(item.product_id) !== -1) {
          log('Offer', offer.id, '| parent line key found:', item.key, 'for product', item.product_id, 'handle', item.handle);
          return { key: item.key, productId: item.product_id, handle: item.handle || null };
        }
      }
      log('Offer', offer.id, '| no parent line key found');
      return { key: null, productId: null, handle: null };
    }

    /* ── Decline tracking ── */

    // Pure helper: merges a new (offerId, sku) entry into a comma-separated
    // "offerId:sku,offerId:sku,..." string. Idempotent — no duplicate entries.
    _mergeDeclinedSkus(existing, offerId, sku) {
      var parts = (existing || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
      var entry = String(offerId) + ':' + String(sku);
      if (parts.indexOf(entry) === -1) parts.push(entry);
      return parts.join(',');
    }

    // Strip every entry for the given offer from a declined-SKU string.
    // Returns the remaining string (possibly empty).
    _stripDeclinedSkusForOffer(existing, offerId) {
      var prefix = String(offerId) + ':';
      var parts = (existing || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean)
        .filter(function (entry) { return entry.indexOf(prefix) !== 0; });
      return parts.join(',');
    }

    // Record a declined SKU on the parent X line item via /cart/change.js.
    // No-op if SKU is missing, no parent X is present, or any required data
    // is unavailable. /cart/change.js replaces properties wholesale, so we
    // read-merge-write the full object.
    _recordDeclinedSku(offerId, sku) {
      var self = this;
      if (!offerId || !sku) {
        log('_recordDeclinedSku: missing offerId or sku, skipping', { offerId: offerId, sku: sku });
        return Promise.resolve(false);
      }
      var offer = this._findOfferById(offerId);
      if (!offer) {
        log('_recordDeclinedSku: offer not found, skipping', offerId);
        return Promise.resolve(false);
      }
      log('_recordDeclinedSku: recording', sku, 'for offer', offerId);

      return fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var parentInfo = self._getParentLineKey(cart, offer);
          if (!parentInfo.key) {
            log('_recordDeclinedSku: no parent X in cart, skipping');
            return false;
          }
          var parentLine = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].key === parentInfo.key) {
              parentLine = cart.items[i];
              break;
            }
          }
          if (!parentLine) {
            log('_recordDeclinedSku: parent line not found in cart by key', parentInfo.key);
            return false;
          }
          var existingProps = parentLine.properties || {};
          var existingDeclined = existingProps._bxgy_declined_skus || '';
          var merged = self._mergeDeclinedSkus(existingDeclined, offerId, sku);
          if (merged === existingDeclined) {
            log('_recordDeclinedSku: SKU', sku, 'already recorded, skipping write');
            return false;
          }
          var newProps = {};
          Object.keys(existingProps).forEach(function (k) { newProps[k] = existingProps[k]; });
          newProps._bxgy_declined_skus = merged;
          log('_recordDeclinedSku: writing _bxgy_declined_skus =', merged, '| parent key:', parentInfo.key);
          self._selfMutating = true;
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parentInfo.key, properties: newProps }),
          })
            .then(function (res) {
              self._selfMutating = false;
              if (!res.ok) {
                console.warn('[BXGY] _recordDeclinedSku: /cart/change.js failed | status:', res.status);
                return false;
              }
              return true;
            })
            .catch(function (err) {
              self._selfMutating = false;
              console.error('[BXGY] _recordDeclinedSku error', err);
              return false;
            });
        })
        .catch(function (err) {
          console.error('[BXGY] _recordDeclinedSku: cart fetch failed', err);
          return false;
        });
    }

    // Clear all declined-SKU entries for a given offer from the parent X line.
    // Called when the customer re-accepts the offer via the "I want the offer" CTA.
    _clearDeclinedSkusForOffer(offerId) {
      var self = this;
      if (!offerId) return Promise.resolve(false);
      var offer = this._findOfferById(offerId);
      if (!offer) return Promise.resolve(false);
      log('_clearDeclinedSkusForOffer: clearing entries for offer', offerId);

      return fetch('/cart.js', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var parentInfo = self._getParentLineKey(cart, offer);
          if (!parentInfo.key) return false;
          var parentLine = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (cart.items[i].key === parentInfo.key) {
              parentLine = cart.items[i];
              break;
            }
          }
          if (!parentLine) return false;
          var existingProps = parentLine.properties || {};
          var existingDeclined = existingProps._bxgy_declined_skus || '';
          if (!existingDeclined) return false;
          var stripped = self._stripDeclinedSkusForOffer(existingDeclined, offerId);
          if (stripped === existingDeclined) return false;
          var newProps = {};
          Object.keys(existingProps).forEach(function (k) {
            if (k === '_bxgy_declined_skus' && !stripped) return;
            newProps[k] = existingProps[k];
          });
          if (stripped) newProps._bxgy_declined_skus = stripped;
          log('_clearDeclinedSkusForOffer: writing _bxgy_declined_skus =', stripped || '(removed)');
          self._selfMutating = true;
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parentInfo.key, properties: newProps }),
          })
            .then(function (res) {
              self._selfMutating = false;
              return res.ok;
            })
            .catch(function (err) {
              self._selfMutating = false;
              console.error('[BXGY] _clearDeclinedSkusForOffer error', err);
              return false;
            });
        })
        .catch(function (err) {
          console.error('[BXGY] _clearDeclinedSkusForOffer: cart fetch failed', err);
          return false;
        });
    }

    _isSameProduct(offer, vid, parentProductId, parentHandle) {
      // Primary: compare numeric product IDs from rewardVariantProductMap (after metafield sync)
      var rewardProductId = offer._rewardVariantProductMap[vid];
      if (parentProductId && rewardProductId != null) {
        var match = Number(rewardProductId) === Number(parentProductId);
        log('Offer', offer.id, '| _isSameProduct via productId: vid', vid, '| rewardProductId:', rewardProductId, '| parentProductId:', parentProductId, '| match:', match);
        return match;
      }
      // Fallback: compare product handles from rewardProducts (works before sync)
      var rewardHandle = offer._rewardVariantHandleMap[vid];
      if (parentHandle && rewardHandle) {
        var match = rewardHandle === parentHandle;
        log('Offer', offer.id, '| _isSameProduct via handle: vid', vid, '| rewardHandle:', rewardHandle, '| parentHandle:', parentHandle, '| match:', match);
        return match;
      }
      log('Offer', offer.id, '| _isSameProduct: no data to compare for vid', vid);
      return false;
    }

    getExistingRewards(cart, offer) {
      var rewards = cart.items.filter(function (item) {
        return (
          item.properties &&
          item.properties._bxgy_offer_id === String(offer.id)
        );
      });
      log('Offer', offer.id, '| existing reward items:', rewards.map(function (r) { return { variant_id: r.variant_id, qty: r.quantity, key: r.key }; }));
      return rewards;
    }

    _findOfferById(id) {
      for (var i = 0; i < this.offers.length; i++) {
        if (String(this.offers[i].id) === String(id)) return this.offers[i];
      }
      return null;
    }

    /* ── Error Notifications ── */

    showCartError(message) {
      log('showCartError:', message);

      // Publish to PUB_SUB_EVENTS.cartError if available (product-form pattern)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'bxgy',
          errors: message,
          message: message,
        });
      }

      // Dispatch a custom event so theme components can react
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: message } }));

      // Show a visible notification to the user
      var existing = document.getElementById('bxgy-error-notification');
      if (existing) existing.remove();

      var el = document.createElement('div');
      el.id = 'bxgy-error-notification';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'assertive');
      el.style.cssText =
        'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:99999;' +
        'background:#fef2f2;color:#991b1b;border:1px solid #fca5a5;border-radius:8px;' +
        'padding:12px 20px;font-size:14px;max-width:90vw;box-shadow:0 4px 12px rgba(0,0,0,0.15);' +
        'display:flex;align-items:center;gap:8px;';
      var safeMessage = document.createElement('div');
      safeMessage.appendChild(document.createTextNode(message));

      el.innerHTML =
        '<span style="flex:1">' + safeMessage.innerHTML + '</span>' +
        '<button type="button" style="background:none;border:none;cursor:pointer;font-size:18px;color:#991b1b;padding:0 4px;" aria-label="Dismiss">&times;</button>';
      el.querySelector('button').addEventListener('click', function () { el.remove(); });

      document.body.appendChild(el);

      // Auto-dismiss after 6 seconds
      setTimeout(function () {
        if (el.parentNode) el.remove();
      }, 6000);
    }

    /* ── Cart Mutations ── */

    addRewardsAll(offer, parentLineKey, parentProductId, parentHandle) {
      var self = this;
      var variantIds = offer._rewardVariantIds.slice();

      if (!variantIds.length) {
        log('Offer', offer.id, '| addRewardsAll: no variant IDs to add');
        return Promise.resolve(false);
      }

      var items = variantIds.map(function (vid) {
        var item = {
          id: vid,
          quantity: 1,
          properties: {
            _bxgy_reward: 'true',
            _bxgy_offer_id: String(offer.id),
            _bxgy_selection_mode: offer.rewardSelectionMode || 'all',
            _bxgy_discount_message: self.discountMessages[offer.discountMessageHandle] || offer.discountMessage || self.t.default_message || 'Special Offer',
          },
        };
        if (offer.orderTag) {
          item.properties._bxgy_order_tag = offer.orderTag;
        }
        // Only nest under parent if the reward is a DIFFERENT product
        if (parentLineKey && !self._isSameProduct(offer, vid, parentProductId, parentHandle)) {
          item.parent_line_key = parentLineKey;
        }
        return item;
      });

      log('Offer', offer.id, '| addRewardsAll: adding', items.length, 'items:', variantIds, '| parent_line_key:', parentLineKey);
      self._selfMutating = true;

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (body) {
              var errorMsg = body.description || body.message || self.t.error_add || 'Could not add the reward item to your cart.';
              console.warn('[BXGY] Failed to add reward variants for offer', offer.id, '| status:', res.status, '|', errorMsg);
              self._selfMutating = false;
              self.showCartError(errorMsg);
              return false;
            });
          }
          log('Offer', offer.id, '| addRewardsAll: success, added', variantIds.length, 'variants');
          self._selfMutating = false;
          log('Offer', offer.id, '| refreshing cart UI + opening drawer');
          self.refreshCartUI(true);
          return true;
        })
        .catch(function (err) {
          console.error('[BXGY] addRewardsAll error for offer', offer.id, err);
          self._selfMutating = false;
          self.showCartError(self.t.error_generic || 'Something went wrong adding the reward item. Please try again.');
          return false;
        });
    }

    addRewardsFromSelection(offer, selections, parentLineKey, parentProductId, parentHandle) {
      var self = this;
      var items = [];

      Object.keys(selections).forEach(function (vid) {
        if (selections[vid] > 0) {
          var numVid = Number(vid);
          var item = {
            id: numVid,
            quantity: selections[vid],
            properties: {
              _bxgy_reward: 'true',
              _bxgy_offer_id: String(offer.id),
              _bxgy_selection_mode: offer.rewardSelectionMode || 'all',
              _bxgy_discount_message: self.discountMessages[offer.discountMessageHandle] || offer.discountMessage || self.t.default_message || 'Special Offer',
            },
          };
          if (offer.orderTag) {
            item.properties._bxgy_order_tag = offer.orderTag;
          }
          // Only nest under parent if the reward is a DIFFERENT product
          if (parentLineKey && !self._isSameProduct(offer, numVid, parentProductId, parentHandle)) {
            item.parent_line_key = parentLineKey;
          }
          items.push(item);
        }
      });

      log('Offer', offer.id, '| addRewardsFromSelection: selections:', selections, '| items to add:', items.length, '| parent_line_key:', parentLineKey);

      if (!items.length) {
        log('Offer', offer.id, '| addRewardsFromSelection: nothing selected');
        return Promise.resolve(false);
      }

      self._selfMutating = true;

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return {}; }).then(function (body) {
              var errorMsg = body.description || body.message || self.t.error_selection || 'Could not add the selected reward to your cart.';
              console.warn('[BXGY] Failed to add selected rewards for offer', offer.id, '| status:', res.status, '|', errorMsg);
              self._selfMutating = false;
              self.showCartError(errorMsg);
              return false;
            });
          }
          log('Offer', offer.id, '| addRewardsFromSelection: success, added', items.length, 'line items');
          self._selfMutating = false;
          log('Offer', offer.id, '| refreshing cart UI + opening drawer');
          self.refreshCartUI(true);
          return true;
        })
        .catch(function (err) {
          console.error('[BXGY] addRewardsFromSelection error for offer', offer.id, err);
          self._selfMutating = false;
          self.showCartError(self.t.error_generic || 'Something went wrong adding the reward item. Please try again.');
          return false;
        });
    }

    removeRewards(cart, offer, excess) {
      var rewards = this.getExistingRewards(cart, offer);
      var updates = {};

      var remaining = excess;
      log('Offer', offer.id, '| removeRewards: removing', excess, 'units across', rewards.length, 'reward line items');
      for (var i = rewards.length - 1; i >= 0 && remaining > 0; i--) {
        var lineQty = rewards[i].quantity;
        if (remaining >= lineQty) {
          log('Offer', offer.id, '| removeRewards: setting key', rewards[i].key, 'to 0 (was', lineQty, ')');
          updates[rewards[i].key] = 0;
          remaining -= lineQty;
        } else {
          var newQty = lineQty - remaining;
          log('Offer', offer.id, '| removeRewards: setting key', rewards[i].key, 'to', newQty, '(was', lineQty, ')');
          updates[rewards[i].key] = newQty;
          remaining = 0;
        }
      }

      if (Object.keys(updates).length === 0) {
        log('Offer', offer.id, '| removeRewards: nothing to remove');
        return Promise.resolve(false);
      }

      var self = this;
      self._selfMutating = true;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      })
        .then(function () {
          var remaining = rewards.length - excess;
          log('Offer', offer.id, '| removeRewards: success, remaining rewards:', Math.max(0, remaining));
          self._selfMutating = false;
          log('Offer', offer.id, '| refreshing cart UI');
          self.refreshCartUI();
          return true;
        })
        .catch(function (err) {
          console.error('[BXGY] Remove error', err);
          self._selfMutating = false;
          return false;
        });
    }

    /* ── Change Selection (cart drawer button) ── */

    installChangeSelectionHandler() {
      var self = this;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('.bxgy-change-selection-btn');
        if (!btn) return;

        var offerId = btn.getAttribute('data-bxgy-offer-id');
        if (!offerId) return;

        log('Change selection button clicked for offer:', offerId);
        var offer = self._findOfferById(offerId);
        if (!offer || offer.rewardSelectionMode !== 'customer_chooses') {
          log('Change selection: offer not found or not customer_chooses mode');
          return;
        }

        e.preventDefault();
        log('Opening edit picker for offer', offerId);
        self.showRewardPickerForEdit(offer);
      });
    }

    /* ── Reward Picker (template-based) ── */

    showRewardPickerForEdit(offer) {
      var self = this;
      log('Offer', offer.id, '| showRewardPickerForEdit: fetching cart for current selections');
      fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var existingRewards = self.getExistingRewards(cart, offer);
          var currentSelections = {};
          existingRewards.forEach(function (item) {
            var vid = item.variant_id;
            currentSelections[vid] = (currentSelections[vid] || 0) + item.quantity;
          });

          // Compute total target qty from qualifying count (same logic as evaluateOffer)
          var qualifyingCount = self.countQualifyingItems(cart, offer);
          var minQualifying = offer.minQualifyingQuantity || 1;
          var targetQty = 0;
          if (qualifyingCount >= minQualifying) {
            targetQty = qualifyingCount * (offer.rewardQuantity || 1);
            if (offer.maxUsesPerOrder && offer.maxUsesPerOrder > 0) {
              targetQty = Math.min(targetQty, offer.maxUsesPerOrder);
            }
          }
          var maxQty = offer.maxRewardSelections > 0 ? Math.min(offer.maxRewardSelections, targetQty) : targetQty;
          log('Offer', offer.id, '| showRewardPickerForEdit: maxQty:', maxQty, '| qualifyingCount:', qualifyingCount, '| targetQty:', targetQty);

          var parentInfo = self._getParentLineKey(cart, offer);
          var parentLineKey = parentInfo.key;
          var parentProductId = parentInfo.productId;
          var parentHandle = parentInfo.handle;
          log('Offer', offer.id, '| showRewardPickerForEdit: current selections:', currentSelections, '| parentLineKey:', parentLineKey);
          self.showRewardPicker(offer, maxQty, self._qualifyingHash(cart, offer), currentSelections, parentLineKey, parentProductId, parentHandle);
        });
    }

    /* ── Variant Availability ── */

    _fetchVariantAvailability(products) {
      var self = this;
      var handleMap = {};
      products.forEach(function (p) {
        if (p.productHandle) {
          handleMap[p.productHandle] = true;
        }
      });
      var handles = Object.keys(handleMap);
      if (!handles.length) {
        log('No product handles found, skipping availability check');
        return Promise.resolve({});
      }

      log('Fetching availability for handles:', handles);
      var fetches = handles.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (res) { return res.ok ? res.json() : null; })
          .catch(function () { return null; });
      });

      return Promise.all(fetches).then(function (results) {
        var availability = {};
        results.forEach(function (productData) {
          if (!productData) return;
          var rawTags = productData.tags || [];
          var tags = (Array.isArray(rawTags) ? rawTags : rawTags.split(',')).map(function (t) { return t.trim().toLowerCase(); });
          var variants = productData.variants || [];

          // Parse warehouse/garage tags
          var gOpsAvailable = tags.indexOf('inventory-unavailable: g-ops') === -1;
          var gOpsEpiAvailable = tags.indexOf('inventory-unavailable: g-ops-epi') === -1;
          var mainFreightAvailable = tags.indexOf('inventory-unavailable: mainfreight') === -1;
          var hasGarageNashville = tags.indexOf('inventory-unavailable: nashville') === -1;
          var hasGarageLondon = tags.indexOf('inventory-unavailable: london') === -1;

          var market = self.currentMarket;

          variants.forEach(function (v) {
            var isSoldOut = !v.available;

            if (!isSoldOut) {
              // Apply market-specific warehouse logic
              if (market === 'us') {
                var usWarehouseAvailable = gOpsAvailable || gOpsEpiAvailable;
                var usGarageAvailable = hasGarageNashville;
                if (!usWarehouseAvailable && !usGarageAvailable) {
                  isSoldOut = true;
                }
              } else if (market === 'europe' || market === 'united-kingdom') {
                var euWarehouseAvailable = mainFreightAvailable;
                var euGarageAvailable = hasGarageLondon;
                if (!euWarehouseAvailable && !euGarageAvailable) {
                  isSoldOut = true;
                }
              }
            }

            availability[v.id] = !isSoldOut;
          });
        });
        log('Variant availability map:', availability);
        return availability;
      });
    }

    showRewardPicker(offer, maxQty, qualifyingHash, existingSelections, parentLineKey, parentProductId, parentHandle) {
      // Prevent duplicate popups
      if (document.querySelector('.bxgy-overlay')) {
        log('Offer', offer.id, '| showRewardPicker: popup already open, skipping');
        return;
      }

      var self = this;
      var products = offer.rewardProducts || [];
      if (!products.length) {
        log('Offer', offer.id, '| showRewardPicker: no reward products defined');
        return;
      }

      var isEdit = !!existingSelections;
      log('Offer', offer.id, '| showRewardPicker: mode:', isEdit ? 'edit' : 'new', '| maxQty:', maxQty, '| products:', products.length);

      // Derive numeric IDs from the GID string
      products.forEach(function (p) {
        p._vid = extractNumericId(p.variantId) || p.numericVariantId;
      });

      // Fetch availability before rendering
      this._fetchVariantAvailability(products).then(function (variantAvailability) {
        self._renderRewardPicker(offer, products, maxQty, qualifyingHash, existingSelections, parentLineKey, parentProductId, parentHandle, isEdit, variantAvailability);
      });
    }

    _renderRewardPicker(offer, products, maxQty, qualifyingHash, existingSelections, parentLineKey, parentProductId, parentHandle, isEdit, variantAvailability) {
      var self = this;
      var soldOutVids = {};

      products.forEach(function (p) {
        var vid = p._vid;
        // If we have availability data and it's false, mark as sold out
        if (Object.keys(variantAvailability).length > 0 && variantAvailability[vid] === false) {
          soldOutVids[vid] = true;
        }
      });
      log('Offer', offer.id, '| sold-out variants:', Object.keys(soldOutVids));

      var selections = {};
      products.forEach(function (p) {
        var vid = p._vid;
        // Reset selection for sold-out variants
        if (soldOutVids[vid]) {
          selections[vid] = 0;
        } else {
          selections[vid] = (existingSelections && existingSelections[vid]) ? existingSelections[vid] : 0;
        }
      });

      function getTotal() {
        var t = 0;
        Object.keys(selections).forEach(function (k) { t += selections[k]; });
        return t;
      }

      // Clone picker template
      var pickerTpl = document.querySelector(PICKER_TEMPLATE);
      if (!pickerTpl) {
        console.error('[BXGY] Picker template not found (' + PICKER_TEMPLATE + ')');
        return;
      }
      var fragment = pickerTpl.content.cloneNode(true);

      var overlay = fragment.querySelector('.bxgy-overlay');
      var heading = fragment.querySelector('[data-bxgy="heading"]');
      var closeBtn = fragment.querySelector('[data-bxgy="close"]');
      var grid = fragment.querySelector('[data-bxgy="grid"]');
      var counter = fragment.querySelector('[data-bxgy="counter"]');
      var submitBtn = fragment.querySelector('[data-bxgy="submit"]');

      var customHeading = self.pickerHeadings[offer.pickerHeadingHandle];
      if (customHeading) {
        heading.textContent = customHeading;
      } else {
        heading.textContent = isEdit ? (self.t.heading_edit || 'Change your reward items') : (self.t.heading_new || 'Choose your reward items');
      }
      submitBtn.textContent = isEdit ? (self.t.submit_update || 'Update Selection') : (self.t.submit_add || 'Add to Cart');

      // Build product cards from template
      var cardTpl = document.querySelector(CARD_TEMPLATE);
      var qtyInputs = {};
      var plusBtns = {};
      var minusBtns = {};

      products.forEach(function (product) {
        var cardFragment = cardTpl.content.cloneNode(true);
        var card = cardFragment.querySelector('.bxgy-product-card');
        var img = cardFragment.querySelector('[data-bxgy="card-img"]');
        var title = cardFragment.querySelector('[data-bxgy="card-title"]');
        var variant = cardFragment.querySelector('[data-bxgy="card-variant"]');
        var price = cardFragment.querySelector('[data-bxgy="card-price"]');
        var minusBtn = cardFragment.querySelector('[data-bxgy="card-minus"]');
        var qtyInput = cardFragment.querySelector('[data-bxgy="card-qty"]');
        var plusBtn = cardFragment.querySelector('[data-bxgy="card-plus"]');

        var vid = product._vid;
        var isSoldOut = !!soldOutVids[vid];

        if (isSoldOut) {
          card.classList.add('sold-out');
        }

        if (product.imageUrl) {
          img.src = product.imageUrl;
          img.alt = product.productTitle;
        } else {
          img.remove();
        }

        title.textContent = product.productTitle;

        if (product.variantTitle && product.variantTitle !== 'Default Title') {
          variant.textContent = product.variantTitle;
        } else {
          variant.remove();
        }

        var originalPrice = parseFloat(product.price) || 0;
        var discountedPrice = originalPrice;
        if (offer.discountType === 'fixed_amount' && offer.discountFixedAmount) {
          discountedPrice = Math.max(0, originalPrice - offer.discountFixedAmount);
        } else if (offer.discountPercentage) {
          discountedPrice = originalPrice * (1 - offer.discountPercentage / 100);
        }
        discountedPrice = Math.max(0, discountedPrice);

        var cs = self.currencySymbol;
        if (discountedPrice === 0) {
          price.textContent = self.t.free || 'FREE';
        } else if (discountedPrice < originalPrice) {
          price.innerHTML = '<s style="color:#6d7175">' + cs + originalPrice.toFixed(2) + '</s> ' + cs + discountedPrice.toFixed(2);
        } else {
          price.textContent = cs + originalPrice.toFixed(2);
        }

        if (isSoldOut) {
          var soldOutLabel = document.createElement('p');
          soldOutLabel.className = 'bxgy-sold-out-label';
          soldOutLabel.textContent = self.t.sold_out || 'Sold Out';
          card.querySelector('.bxgy-product-info').appendChild(soldOutLabel);
        }

        qtyInput.value = String(selections[vid]);
        qtyInput.setAttribute('aria-label', (self.t.quantity_label || 'Quantity for __TITLE__').replace('__TITLE__', product.productTitle));
        minusBtn.disabled = isSoldOut || selections[vid] <= 0;
        plusBtn.disabled = isSoldOut;

        qtyInputs[vid] = qtyInput;
        plusBtns[vid] = plusBtn;
        minusBtns[vid] = minusBtn;

        if (!isSoldOut) {
          plusBtn.addEventListener('click', function () {
            if (getTotal() >= maxQty) return;
            selections[vid]++;
            qtyInput.value = String(selections[vid]);
            log('Offer', offer.id, '| picker: + clicked for variant', vid, '| new qty:', selections[vid], '| total:', getTotal());
            updateUI();
          });

          minusBtn.addEventListener('click', function () {
            if (selections[vid] <= 0) return;
            selections[vid]--;
            qtyInput.value = String(selections[vid]);
            log('Offer', offer.id, '| picker: - clicked for variant', vid, '| new qty:', selections[vid], '| total:', getTotal());
            updateUI();
          });
        }

        grid.appendChild(cardFragment);
      });

      var counterTpl = self.t.counter || 'Selected: __COUNT__ of __MAX__';
      function counterText(n) {
        return counterTpl.replace('__COUNT__', n).replace('__MAX__', maxQty);
      }

      // Update counter with initial state
      var initTotal = getTotal();
      counter.textContent = counterText(initTotal);
      submitBtn.disabled = initTotal < 1;

      // Append to DOM
      document.body.appendChild(fragment);
      log('Offer', offer.id, '| picker: popup rendered');

      // Re-query overlay from DOM (fragment is now empty after append)
      var liveOverlay = document.querySelector('.bxgy-overlay');

      function updateUI() {
        var total = getTotal();
        counter.textContent = counterText(total);
        submitBtn.disabled = total < 1;

        products.forEach(function (product) {
          var vid = product._vid;
          if (soldOutVids[vid]) return; // Keep sold-out buttons permanently disabled
          minusBtns[vid].disabled = selections[vid] <= 0;
          plusBtns[vid].disabled = total >= maxQty;
        });
      }

      if (isEdit) updateUI();

      function closePopup() {
        log('Offer', offer.id, '| picker: popup closed');
        if (liveOverlay) liveOverlay.remove();
      }

      closeBtn.addEventListener('click', function () {
        if (!isEdit) {
          log('Offer', offer.id, '| picker: user dismissed without adding, setting opt-out');
          self.optOuts.set(offer.id, qualifyingHash);
          self._saveSessionOptOut(offer.id, null, qualifyingHash);
        }
        closePopup();
        // Re-evaluate so remaining offers get processed
        log('Offer', offer.id, '| picker: dismissed, scheduling re-evaluate for remaining offers');
        self.debouncedEvaluate();
      });

      liveOverlay.addEventListener('click', function (e) {
        if (e.target === liveOverlay) {
          if (!isEdit) {
            log('Offer', offer.id, '| picker: overlay clicked (dismissed), setting opt-out');
            self.optOuts.set(offer.id, qualifyingHash);
            self._saveSessionOptOut(offer.id, null, qualifyingHash);
          }
          closePopup();
          // Re-evaluate so remaining offers get processed
          log('Offer', offer.id, '| picker: dismissed via overlay, scheduling re-evaluate for remaining offers');
          self.debouncedEvaluate();
        }
      });

      submitBtn.addEventListener('click', function () {
        log('Offer', offer.id, '| picker: add/update button clicked | selections:', JSON.stringify(selections));
        submitBtn.disabled = true;
        submitBtn.textContent = isEdit ? (self.t.submit_updating || 'Updating...') : (self.t.submit_adding || 'Adding...');

        var finishAdd = function () {
          return self.addRewardsFromSelection(offer, selections, parentLineKey, parentProductId, parentHandle).then(function () {
            self.optOuts.delete(offer.id);
            self._removeSessionOptOut(offer.id);
            closePopup();
            // Re-evaluate so remaining offers get processed
            log('Offer', offer.id, '| picker: rewards added, scheduling re-evaluate for remaining offers');
            self.debouncedEvaluate();
          });
        };

        if (isEdit) {
          log('Offer', offer.id, '| picker: edit mode — removing existing rewards first');
          fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
            .then(function (res) { return res.json(); })
            .then(function (cart) {
              var existingRewards = self.getExistingRewards(cart, offer);
              var totalRewardQty = existingRewards.reduce(function (sum, item) { return sum + item.quantity; }, 0);
              if (totalRewardQty > 0) {
                log('Offer', offer.id, '| picker: removing', totalRewardQty, 'existing reward units before re-adding');
                return self.removeRewards(cart, offer, totalRewardQty);
              }
              return true;
            })
            .then(function (removed) {
              if (removed === false) {
                log('Offer', offer.id, '| picker: remove failed, aborting update');
                submitBtn.disabled = false;
                submitBtn.textContent = self.t.submit_update || 'Update Selection';
                return;
              }
              log('Offer', offer.id, '| picker: existing rewards removed, adding new selections');
              return finishAdd();
            });
        } else {
          finishAdd();
        }
      });
    }

    /* ── Free Gift CTA ── */

    _injectFreeGiftCTAs(forceClean) {
      if (this._injectingCTAs) return;
      this._injectingCTAs = true;
      var self = this;

      // forceClean: remove all CTAs first (used after evaluate when opt-out state may have changed)
      // Without forceClean: idempotent — only adds missing CTAs, skips rows that already have one
      if (forceClean) {
        log('_injectFreeGiftCTAs: forceClean — removing all existing CTAs');
        var existing = document.querySelectorAll('.bxgy-free-gift-cta');
        for (var i = 0; i < existing.length; i++) {
          existing[i].remove();
        }
      }

      fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          for (var oi = 0; oi < self.offers.length; oi++) {
            var offer = self.offers[oi];
            var mode = offer.rewardSelectionMode || 'all';

            // Must be opted out (in-memory or session)
            var memoryOpt = self.optOuts.get(offer.id);
            var sessionOpt = self._sessionOptOuts[String(offer.id)];
            if (!memoryOpt && !sessionOpt) continue;

            // Must have qualifying items
            var qualCount = self.countQualifyingItems(cart, offer);
            if (qualCount < (offer.minQualifyingQuantity || 1)) continue;

            // Must NOT already have all expected rewards in cart
            var existingRewards = self.getExistingRewards(cart, offer);
            if (mode === 'all') {
              // Hide CTA only when ALL unique reward variants are present in cart
              var presentVids = {};
              for (var ei = 0; ei < existingRewards.length; ei++) {
                presentVids[String(existingRewards[ei].variant_id)] = true;
              }
              var allPresent = true;
              for (var vi = 0; vi < offer._rewardVariantIds.length; vi++) {
                if (!presentVids[String(offer._rewardVariantIds[vi])]) { allPresent = false; break; }
              }
              if (allPresent) continue;
            } else {
              // customer_chooses: hide CTA if any rewards exist (original behavior)
              if (existingRewards.length > 0) continue;
            }

            // Find the first qualifying item's cart index (1-based)
            var cartIndex = -1;
            for (var ci = 0; ci < cart.items.length; ci++) {
              var item = cart.items[ci];
              if (item.properties && item.properties._bxgy_reward === 'true') continue;
              if (offer._qualifyingIds.indexOf(item.product_id) !== -1) {
                cartIndex = ci + 1;
                break;
              }
            }
            if (cartIndex === -1) continue;

            var row = document.getElementById('CartDrawer-Item-' + cartIndex);
            if (!row) {
              log('_injectFreeGiftCTAs: #CartDrawer-Item-' + cartIndex + ' not found');
              continue;
            }

            // Idempotent: skip if CTA already exists for this offer in this row
            if (row.querySelector('.bxgy-free-gift-cta')) continue;

            var detailsTd = row.querySelector('td .cart-drawer__quantity');
            if (!detailsTd) {
              log('_injectFreeGiftCTAs: .cart-drawer__quantity not found in row', cartIndex);
              continue;
            }

            var ctaDiv = document.createElement('div');
            ctaDiv.className = 'bxgy-free-gift-cta';
            var ctaBtn = document.createElement('button');
            ctaBtn.type = 'button';
            ctaBtn.className = 'bxgy-free-gift-cta__link';
            ctaBtn.textContent = self.t.free_gift_cta || 'Eligible for a special offer';
            ctaBtn.setAttribute('data-bxgy-offer-id', offer.id);
            ctaDiv.appendChild(ctaBtn);

            // Insert CTA after the quantity div
            detailsTd.parentNode.insertBefore(ctaDiv, detailsTd.nextSibling);
            log('_injectFreeGiftCTAs: injected CTA for offer', offer.id, 'at CartDrawer-Item-' + cartIndex);

            // Click handler — clear opt-out, remove CTAs, then re-add (all mode) or open picker (customer_chooses)
            (function (boundOffer) {
              ctaBtn.addEventListener('click', function () {
                var clickMode = boundOffer.rewardSelectionMode || 'all';
                log('CTA clicked for offer', boundOffer.id, '| mode:', clickMode, '| clearing opt-out and declined SKUs');
                self.optOuts.delete(boundOffer.id);
                self._removeSessionOptOut(boundOffer.id);
                // Customer re-accepted — strip this offer's declined SKUs from
                // the parent X line. Fire-and-forget; the add/picker flow
                // below operates on a separately fetched cart.
                // Known edge case: in customer_chooses mode, if the customer
                // picks fewer items than they previously declined, this
                // over-clears. Acceptable for now; can be addressed with a
                // reconciliation step in a follow-up if needed.
                self._clearDeclinedSkusForOffer(boundOffer.id);

                // Remove all CTAs
                var allCtas = document.querySelectorAll('.bxgy-free-gift-cta');
                for (var r = 0; r < allCtas.length; r++) {
                  allCtas[r].remove();
                }

                if (clickMode === 'all') {
                  // Add only missing reward variants
                  fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
                    .then(function (res) { return res.json(); })
                    .then(function (freshCart) {
                      var existingVids = {};
                      self.getExistingRewards(freshCart, boundOffer).forEach(function (item) {
                        existingVids[String(item.variant_id)] = true;
                      });
                      var missingVids = boundOffer._rewardVariantIds.filter(function (vid) {
                        return !existingVids[String(vid)];
                      });
                      if (!missingVids.length) {
                        log('CTA click (all): no missing variants, just re-evaluating');
                        self.debouncedEvaluate();
                        return;
                      }
                      var parentInfo = self._getParentLineKey(freshCart, boundOffer);
                      var items = missingVids.map(function (vid) {
                        var item = {
                          id: vid,
                          quantity: 1,
                          properties: {
                            _bxgy_reward: 'true',
                            _bxgy_offer_id: String(boundOffer.id),
                            _bxgy_discount_message: self.discountMessages[boundOffer.discountMessageHandle] || boundOffer.discountMessage || self.t.default_message || 'Special Offer',
                          },
                        };
                        if (boundOffer.orderTag) {
                          item.properties._bxgy_order_tag = boundOffer.orderTag;
                        }
                        if (parentInfo.key && !self._isSameProduct(boundOffer, vid, parentInfo.productId, parentInfo.handle)) {
                          item.parent_line_key = parentInfo.key;
                        }
                        return item;
                      });
                      log('CTA click (all): adding', missingVids.length, 'missing variants:', missingVids);
                      self._selfMutating = true;
                      return fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ items: items }),
                      }).then(function (res) {
                        if (!res.ok) {
                          self._selfMutating = false;
                          return res.json().catch(function () { return {}; }).then(function (body) {
                            var errorMsg = body.description || body.message || self.t.error_add || 'Could not add the reward item to your cart.';
                            console.warn('[BXGY] CTA click (all): failed to add variants for offer', boundOffer.id, '| status:', res.status, '|', errorMsg);
                            self.showCartError(errorMsg);
                          });
                        }
                        self._selfMutating = false;
                        log('CTA click (all): success, refreshing cart UI');
                        self.refreshCartUI(true);
                      });
                    })
                    .catch(function (err) {
                      self._selfMutating = false;
                      console.error('[BXGY] CTA click (all): error for offer', boundOffer.id, err);
                      self.showCartError(self.t.error_generic || 'Something went wrong adding the reward item. Please try again.');
                      self.debouncedEvaluate();
                    });
                } else {
                  // customer_chooses: fetch fresh cart and open picker
                  fetch('/cart.js', { method: 'GET', headers: { 'Content-Type': 'application/json' } })
                    .then(function (res) { return res.json(); })
                    .then(function (freshCart) {
                      var rewardQty = boundOffer.rewardQuantity || 1;
                      var qualCount = self.countQualifyingItems(freshCart, boundOffer);
                      var targetQty = qualCount * rewardQty;
                      if (boundOffer.maxUsesPerOrder && boundOffer.maxUsesPerOrder > 0) {
                        targetQty = Math.min(targetQty, boundOffer.maxUsesPerOrder);
                      }
                      var maxQty = boundOffer.maxRewardSelections > 0 ? Math.min(boundOffer.maxRewardSelections, targetQty) : targetQty;
                      var parentInfo = self._getParentLineKey(freshCart, boundOffer);
                      self.showRewardPicker(boundOffer, maxQty, self._qualifyingHash(freshCart, boundOffer), undefined, parentInfo.key, parentInfo.productId, parentInfo.handle);
                    })
                    .catch(function (err) {
                      log('CTA click: failed to open picker', err);
                      self.debouncedEvaluate();
                    });
                }
              });
            })(offer);
          }

          self._injectingCTAs = false;
        })
        .catch(function (err) {
          log('_injectFreeGiftCTAs error:', err);
          self._injectingCTAs = false;
        });
    }

    /* ── Cart UI Refresh ── */

    refreshCartUI(openDrawer) {
      log('refreshCartUI: fetching sections cart-drawer, cart-icon-bubble | openDrawer:', !!openDrawer);
      fetch('/?sections=cart-drawer,cart-icon-bubble')
        .then(function (res) { return res.json(); })
        .then(function (sections) {
          var drawerHTML = sections['cart-drawer'];
          var bubbleHTML = sections['cart-icon-bubble'];

          if (drawerHTML) {
            var drawerEl = document.getElementById('CartDrawer');
            if (drawerEl) {
              var temp = document.createElement('div');
              temp.innerHTML = drawerHTML;
              var newDrawer = temp.querySelector('#CartDrawer');
              if (newDrawer) {
                drawerEl.innerHTML = newDrawer.innerHTML;
                log('refreshCartUI: cart drawer updated');
              } else {
                log('refreshCartUI: #CartDrawer not found in fetched HTML');
              }
            } else {
              log('refreshCartUI: #CartDrawer element not in DOM');
            }
          } else {
            log('refreshCartUI: no cart-drawer section in response');
          }

          if (bubbleHTML) {
            var bubbleEl = document.querySelector('.cart-icon-bubble');
            if (bubbleEl) {
              var temp2 = document.createElement('div');
              temp2.innerHTML = bubbleHTML;
              var newBubble = temp2.querySelector('.cart-icon-bubble');
              if (newBubble) {
                bubbleEl.innerHTML = newBubble.innerHTML;
                log('refreshCartUI: cart icon bubble updated');
              } else {
                log('refreshCartUI: .cart-icon-bubble not found in fetched HTML');
              }
            } else {
              log('refreshCartUI: .cart-icon-bubble element not in DOM');
            }
          } else {
            log('refreshCartUI: no cart-icon-bubble section in response');
          }

          log('refreshCartUI: dispatching cart:refresh event');
          document.dispatchEvent(new CustomEvent('cart:refresh'));

          if (openDrawer) {
            var cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              log('refreshCartUI: opening cart drawer');
              cartDrawer.open();
            } else {
              log('refreshCartUI: cart-drawer element not found or no open() method');
            }
          }
        })
        .catch(function (err) {
          console.error('[BXGY] UI refresh error', err);
        });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    log('DOM not ready, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', function () {
      log('DOMContentLoaded fired');
      new BxgyManager();
    });
  } else {
    log('DOM already ready, initializing immediately');
    new BxgyManager();
  }
})();
